import React, { useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Plus, Trash2, GripVertical, Upload, ImageIcon } from 'lucide-react';

export const Field = ({ label, value, onChange, placeholder }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-slate-700 text-sm font-semibold">{label}</Label>}
    <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

export const Area = ({ label, value, onChange, rows = 3, placeholder }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-slate-700 text-sm font-semibold">{label}</Label>}
    <Textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} />
  </div>
);

// Editable list of plain strings
export const StringList = ({ label, items = [], onChange, placeholder }) => {
  const update = (i, v) => { const n = [...items]; n[i] = v; onChange(n); };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, '']);
  return (
    <div className="space-y-2">
      {label && <Label className="text-slate-700 text-sm font-semibold">{label}</Label>}
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-2">
          <Textarea value={it} onChange={(e) => update(i, e.target.value)} rows={2} placeholder={placeholder} className="flex-1" />
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} className="text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0">
            <Trash2 size={16} />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1 text-brand-blue border-brand-blue/30">
        <Plus size={15} /> Add item
      </Button>
    </div>
  );
};

// Editable list of objects. `columns` = [{ key, label, type: 'text'|'area'|'stringlist' }]
export const Repeater = ({ label, items = [], columns, onChange, template }) => {
  const updateItem = (i, key, val) => {
    const n = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    onChange(n);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { ...template }]);
  return (
    <div className="space-y-3">
      {label && <Label className="text-slate-800 font-bold">{label}</Label>}
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3 relative">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-xs font-semibold text-slate-400"><GripVertical size={14} /> #{i + 1}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(i)} className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-1">
              <Trash2 size={15} /> Remove
            </Button>
          </div>
          {columns.map((c) => (
            c.type === 'area' ? (
              <Area key={c.key} label={c.label} value={it[c.key]} onChange={(v) => updateItem(i, c.key, v)} />
            ) : c.type === 'stringlist' ? (
              <StringList key={c.key} label={c.label} items={it[c.key] || []} onChange={(v) => updateItem(i, c.key, v)} />
            ) : c.type === 'image' ? (
              <ImageUpload key={c.key} label={c.label} value={it[c.key]} onChange={(v) => updateItem(i, c.key, v)} />
            ) : (
              <Field key={c.key} label={c.label} value={it[c.key]} onChange={(v) => updateItem(i, c.key, v)} />
            )
          ))}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1 text-brand-green border-brand-green/40">
        <Plus size={15} /> Add {label ? label.replace(/s$/, '') : 'item'}
      </Button>
    </div>
  );
};

// Image upload: resizes client-side to keep payload small, stores as base64 data URL.
export const ImageUpload = ({ label, value, onChange }) => {
  const inputRef = useRef();
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxW = 600;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        onChange(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-2">
      {label && <Label className="text-slate-700 text-sm font-semibold">{label}</Label>}
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
          {value ? <img src={value} alt="preview" className="w-full h-full object-cover" /> : <ImageIcon className="text-slate-300" size={26} />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} className="gap-1 text-brand-blue border-brand-blue/30">
            <Upload size={15} /> {value ? 'Change Photo' : 'Upload Photo'}
          </Button>
          {value && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')} className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-1">
              <Trash2 size={15} /> Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const Card = ({ title, description, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
    {title && (
      <div>
        <h3 className="font-display font-bold text-lg text-brand-bluedark">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
      </div>
    )}
    {children}
  </div>
);
